import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Box, Button, Paper, Typography } from '@mui/material';
import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';

import { UserContext } from '../../context/UserContext';

/**
 * ExpiredGuard -- inline content-area guard that blocks feature routes when the
 * client's subscription has expired.  Unlike the old full-page ExpiredOverlay,
 * this renders inside the page content area so header and sidebar stay
 * accessible (users can still log out, navigate to plans, etc.).
 *
 * Usage (in Router.js):
 *   { path: '/analytics', element: <ExpiredGuard><AnalyticsDashboard /></ExpiredGuard> }
 *
 * Superadmins, customer_success users, and impersonating users bypass this guard.
 */
const ExpiredGuard = ({ children }) => {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();

  if (!user) return null;

  // Superadmins, customer_success, and impersonating users bypass
  if (user.permissions?.superadmin || user.permissions?.customer_success || user.original_user_id) {
    return children;
  }

  const subscriptionStatus =
    user.subscription_status ||
    user.client?.subscription_status;

  if (subscriptionStatus !== 'expired') return children;

  return (
    <Box
      role="alert"
      aria-live="assertive"
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 'calc(100vh - 200px)',
      }}
    >
      <Paper
        elevation={6}
        sx={{
          p: 5,
          maxWidth: 520,
          width: '90%',
          textAlign: 'center',
          borderRadius: 3,
        }}
      >
        <LockOutlinedIcon aria-hidden="true" sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
        <Typography variant="h5" gutterBottom fontWeight={700}>
          Tu suscripción venció
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Activa tu plan para seguir usando Isi.
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={() => navigate('/plans')}
        >
          Ver planes
        </Button>
      </Paper>
    </Box>
  );
};

export default ExpiredGuard;
